import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { saveAuthReturnPath } from '../../lib/authReturnPath';
import { isDisposableEmail } from '../../utils/disposableEmails';
import { normalizeEmail } from '../../utils/normalizeEmail';

import RegistrationForm from './components/RegistrationForm';
import SocialRegistration from './components/SocialRegistration';
import BenefitsShowcase from './components/BenefitsShowcase';
import SuccessModal from './components/SuccessModal';

const VOID_BG = '#080810';
const CARD_BG = '#0D0D1A';
const CYAN = '#00FFE5';
const ICE = '#E8F4FF';
const MUTED = '#8899AA';
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const ORBITRON = "'Orbitron', monospace";

const UserRegistration = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registrationError, setRegistrationError] = useState('');
  const { signUp, signInWithOAuth } = useAuth();

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    try {
      setRegistrationError('');
      // === ANTI-ABUSE LAYER 2 ===
      const rawEmail = formData?.email?.trim().toLowerCase() || '';
      if (!rawEmail || !rawEmail.includes('@')) {
        setRegistrationError('Please enter a valid email address.');
        setIsLoading(false);
        return;
      }
      if (isDisposableEmail(rawEmail)) {
        setRegistrationError("This email provider isn't supported. Please use a permanent email address.");
        setIsLoading(false);
        return;
      }
      // === END ANTI-ABUSE LAYER 2 ===

      const { error } = await signUp(formData?.email, formData?.password, {
        data: { first_name: formData?.firstName, last_name: formData?.lastName },
        emailRedirectTo: `${window.location.origin}/user-login`,
      });
      if (error) throw error;
      setRegisteredEmail(formData?.email);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Registration failed:', error);
      setRegistrationError(error?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialRegister = async (provider) => {
    if (provider === 'google') {
      saveAuthReturnPath('/operations');
      const { error } = await signInWithOAuth('google', {
        redirectTo: `${window.location.origin}/user-login`,
      });
      if (error) setRegistrationError(error.message);
      return;
    }
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setRegisteredEmail(`user@${provider}.com`);
      setShowSuccessModal(true);
    } catch (error) {
      console.error(`${provider} registration failed:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen page-fade-in" style={{ background: VOID_BG, color: ICE, fontFamily: MONO }}>
      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(0,255,229,0.2)', background: 'rgba(8,8,16,0.95)', backdropFilter: 'blur(12px)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-3 transition-opacity hover:opacity-80"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <span style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '1.1rem', color: ICE }}>
                &lt; RECALL<span style={{ color: CYAN }}>CORE</span>™
              </span>
            </button>
            <button onClick={() => navigate('/user-login')}
              style={{ color: CYAN, fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.color = ICE}
              onMouseLeave={e => e.currentTarget.style.color = CYAN}>
              ALREADY SIGNED UP? <span style={{ textDecoration: 'underline' }}>SIGN IN</span>
            </button>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-6">
            <div className="max-w-md mx-auto lg:mx-0">
              <SocialRegistration onSocialRegister={handleSocialRegister} isLoading={isLoading} />
              {registrationError && (
                <div style={{ padding: '12px', marginBottom: '16px', border: '1px solid rgba(255,80,80,0.4)', background: 'rgba(255,80,80,0.05)', color: '#ff5050', fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                  {registrationError}
                </div>
              )}
              <RegistrationForm onSubmit={handleFormSubmit} isLoading={isLoading} />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="sticky top-8">
              <BenefitsShowcase />
            </div>
          </div>
        </div>
      </main>
      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} userEmail={registeredEmail} />
      {/* Footer */}
      <footer className="mt-16 py-8 px-4 text-center" style={{ borderTop: '1px solid rgba(0,255,229,0.15)', background: VOID_BG }}>
        <div className="flex justify-center gap-6 mb-4">
          {['Terms of Service', 'Privacy Policy', 'Help Center']?.map(link => (
            <button key={link} style={{ color: 'rgba(0,255,229,0.4)', fontSize: '0.65rem', fontFamily: MONO, background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em' }}
              onMouseEnter={e => e.currentTarget.style.color = CYAN}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(0,255,229,0.4)'}>{link}</button>
          ))}
        </div>
        <p style={{ color: 'rgba(0,255,229,0.6)', fontSize: '0.7rem', fontFamily: MONO }}>© 2026 RecallCore. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default UserRegistration;
